/* Custom ingredient carousel — arrows, progress bar, per-card
   detail drawer with collapsible sections. Clone-based infinite
   loop off (we use a simple bounded rail so the progress bar
   maps 1:1 to scroll position). */
(function () {
  if (customElements.get('ingredient-carousel')) return;

  class IngredientCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ic-track]');
      if (!this.track) return;

      this.cards = Array.from(this.track.querySelectorAll('[data-ic-card]'));
      this.prevBtn = this.querySelector('[data-ic-prev]');
      this.nextBtn = this.querySelector('[data-ic-next]');
      this.progressFill = this.querySelector('[data-ic-progress-fill]');
      this.drawer = this.querySelector('[data-ic-drawer]');

      this.prevBtn?.addEventListener('click', () => this._scrollByCards(-1));
      this.nextBtn?.addEventListener('click', () => this._scrollByCards(1));

      this._setupDrag();
      this._setupScrollSync();
      this._setupOpeners();
      this._setupDrawer();
      this._setupAutoRotate();

      window.addEventListener('resize', () => this._updateProgress(), { passive: true });
      requestAnimationFrame(() => this._updateProgress());
    }

    _scrollByCards(direction) {
      const card = this.cards[0];
      if (!card) return;
      const styles = getComputedStyle(this.track);
      const gap = parseInt(styles.columnGap || styles.gap, 10) || 0;
      const w = card.getBoundingClientRect().width;
      this.track.scrollBy({ left: (w + gap) * direction, behavior: 'smooth' });
    }

    _setupScrollSync() {
      let ticking = false;
      const update = () => {
        ticking = false;
        this._updateProgress();
        this._updateArrows();
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      this.track.addEventListener('scroll', onScroll, { passive: true });
    }

    _updateProgress() {
      if (!this.progressFill) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const raw = max > 0 ? this.track.scrollLeft / max : 1;
      // Extend the fill by the visible-ratio so the last position reads 100%
      // rather than "one card in, still short of the end".
      const visible = this.track.clientWidth / this.track.scrollWidth;
      const p = Math.min(1, raw * (1 - visible) + visible);
      this.progressFill.style.setProperty('--ic-progress-w', (p * 100).toFixed(2) + '%');
    }

    _updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max - 2;
    }

    _setupDrag() {
      let down = false, startX = 0, startScroll = 0, moved = false;
      const onDown = (e) => {
        if (e.type === 'mousedown' && e.button !== 0) return;
        down = true; moved = false;
        startX = e.pageX - this.track.getBoundingClientRect().left;
        startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
      };
      const onMove = (e) => {
        if (!down) return;
        const x = e.pageX - this.track.getBoundingClientRect().left;
        const walk = x - startX;
        if (Math.abs(walk) > 4) moved = true;
        this.track.scrollLeft = startScroll - walk;
      };
      const onUp = () => {
        if (!down) return;
        down = false;
        this.track.classList.remove('is-dragging');
      };
      this.track.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
      this.track.addEventListener('mousedown', onDown);
      this.track.addEventListener('mousemove', onMove);
      this.track.addEventListener('mouseup', onUp);
      this.track.addEventListener('mouseleave', onUp);
    }

    /* === Drawer === */
    _setupOpeners() {
      this.querySelectorAll('[data-ic-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.icOpen;
          this._openDrawer(id);
        });
      });
    }

    _setupDrawer() {
      if (!this.drawer) return;
      this.drawer.querySelectorAll('[data-ic-drawer-close]').forEach((el) => {
        el.addEventListener('click', () => this._closeDrawer());
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this._closeDrawer();
      });
      // Collapsible section toggles inside the drawer.
      this.drawer.querySelectorAll('[data-ic-collapse]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const wrap = btn.closest('.custom-ic__collapsible');
          if (wrap) wrap.classList.toggle('is-open');
        });
      });
    }

    _openDrawer(id) {
      if (!this.drawer) return;
      // Portal to <body> once so the section's containment can't trap
      // the fixed-positioned panel (same fix as customer-reviews modal).
      if (this.drawer.parentElement !== document.body) {
        const styles = getComputedStyle(this);
        [
          '--ic-drawer-bg', '--ic-drawer-text',
          '--ic-drawer-backdrop', '--ic-drawer-padding',
        ].forEach((v) => {
          const val = styles.getPropertyValue(v).trim();
          if (val) this.drawer.style.setProperty(v, val);
        });
        document.body.appendChild(this.drawer);
      }
      // Show only the panel matching the clicked card; hide others.
      this.drawer.querySelectorAll('[data-ic-panel]').forEach((p) => {
        p.hidden = p.dataset.icPanel !== id;
      });
      // Reset collapsibles closed on open.
      this.drawer.querySelectorAll('.custom-ic__collapsible.is-open')
        .forEach((c) => c.classList.remove('is-open'));
      this.drawer.classList.add('is-open');
      document.documentElement.classList.add('custom-ic-drawer-open');
      const closeBtn = this.drawer.querySelector('[data-ic-drawer-close]');
      closeBtn?.focus({ preventScroll: true });
    }

    _closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('custom-ic-drawer-open');
    }

    _setupAutoRotate() {
      const enabled = this.dataset.autorotate === 'true';
      const interval = parseInt(this.dataset.interval, 10) || 5000;
      const pause = this.dataset.pauseOnHover === 'true';
      if (!enabled || this.cards.length <= 1) return;
      this._paused = false;
      const tick = () => {
        if (this._paused) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= max - 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this._scrollByCards(1);
        }
      };
      this._timer = setInterval(tick, interval);
      if (pause) {
        this.addEventListener('mouseenter', () => (this._paused = true));
        this.addEventListener('mouseleave', () => (this._paused = false));
        this.addEventListener('touchstart', () => (this._paused = true), { passive: true });
        this.addEventListener('touchend', () => setTimeout(() => (this._paused = false), 2000), { passive: true });
      }
    }

    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
    }
  }

  customElements.define('ingredient-carousel', IngredientCarousel);
})();
